// ═══════════════════════════════════════════════════════════════════════════
// EXPORT / IMPORT helpers
// Migration S4.1 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════

interface RevisionLite {
  id: number;
  is_closed?: boolean;
  date_end?: string | null;
}

interface ProjectLite {
  id: number;
  name?: string;
  code?: string;
}

interface ExportImportOpts {
  importOnly?: boolean;
}

interface ExampleSet {
  json: string;
  csv: string;
}

declare global {
  interface Window {
    showExportRevisionModal: typeof showExportRevisionModal;
    showExportImportModal: typeof showExportImportModal;
    EXPORT_EXAMPLES: typeof EXPORT_EXAMPLES;
  }
}

function showExportRevisionModal(rev: RevisionLite, _project?: ProjectLite): void {
  const isClosed = rev.is_closed;
  const closedWarning = isClosed
    ? `<div style="margin-bottom:12px;padding:10px 12px;background:#fffbeb;border-radius:6px;border:1px solid #fde68a;font-size:12px;color:#92400e">
        ${t('exportImport.revClosed', { date: rev.date_end || '?' })}
       </div>`
    : '';

  const body = `
    ${closedWarning}
    <div class="form-group">
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px">
        <input type="checkbox" id="xls-include-actions" checked>
        ${esc(t('exportImport.includeActions'))}
      </label>
      <p style="font-size:11px;color:var(--text3);margin:4px 0 0 24px">
        ${esc(t('exportImport.actionsHelp'))}
      </p>
    </div>`;

  openModal(
    t('exportImport.exportRevTitle'),
    body,
    () => {
      const checkbox = document.getElementById('xls-include-actions') as HTMLInputElement | null;
      const includeActions = checkbox?.checked ?? true;
      const exportDt = new Date().toISOString();
      const userId = currentUserId();
      const url = api.exportRevisionXlsxUrl(rev.id, includeActions, userId, exportDt);
      // Trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = '';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      closeModal();
    },
    t('exportImport.btnDownload'),
  );
}

function showExportImportModal(
  entityKey: string,
  entityLabel: string,
  exampleJson: string,
  exampleCsv: string,
  opts?: ExportImportOpts,
): void {
  // Les liens d'export restent en <a href> direct car le navigateur gère
  // nativement le download d'une réponse binaire (ne passe pas par api.ts).
  // L'import utilise api.upload qui centralise la gestion 401/erreur.
  //
  // opts.importOnly : si true, on cache les boutons d'export. Utilisé sur
  // les pages où l'export est exposé séparément (cf. #admin/risk-lists qui
  // a son propre export en bouton du header).
  const importOnly = !!(opts && opts.importOnly);
  const exportBase = `/api/v1/export/${entityKey}/export`;
  const importPath = `/export/${entityKey}/import`;

  const exportBlock = importOnly
    ? ''
    : `
    <div style="display:flex;gap:8px;margin-bottom:16px">
      <a href="${exportBase}?fmt=json" download class="btn btn-secondary btn-sm">${esc(t('exportImport.btnExportJson'))}</a>
      <a href="${exportBase}?fmt=csv"  download class="btn btn-secondary btn-sm">${esc(t('exportImport.btnExportCsv'))}</a>
    </div>`;

  // Question plafond contractuel (users uniquement, facturation B-2) : posée
  // AVANT l'import. Cochée → l'import relève max_users en cas de dépassement ;
  // décochée → l'import s'arrête au plafond. Accepter le complément de
  // facturation exige le droit `ia_credits.buy` (le même que l'achat de crédits
  // IA dans « Mon compte ») : sans lui, pas de case, juste une note.
  const canAcceptBilling = !!(window.hasPermission && window.hasPermission('ia_credits.buy'));
  let overageBlock = '';
  if (entityKey === 'users') {
    overageBlock = canAcceptBilling
      ? `
    <div style="border-top:1px solid var(--border);margin-top:12px;padding-top:12px">
      <label class="inline-check" style="display:flex;gap:8px;align-items:flex-start;cursor:pointer">
        <input type="checkbox" id="ei-allow-overage" style="margin-top:2px">
        <span style="font-size:12px">${esc(t('exportImport.users.overageAccept'))}</span>
      </label>
      <div style="font-size:11px;color:var(--text3);margin-top:4px">${esc(t('exportImport.users.overageHint'))}</div>
    </div>`
      : `
    <div style="border-top:1px solid var(--border);margin-top:12px;padding-top:12px;font-size:11px;color:var(--text3)">${esc(t('exportImport.users.overageNoRight'))}</div>`;
  }

  const body = `
    ${exportBlock}
    <div${importOnly ? '' : ' style="border-top:1px solid var(--border);padding-top:12px"'}>
      <div style="font-size:13px;font-weight:500;margin-bottom:6px">${esc(t('exportImport.importTitle'))}</div>
      <input type="file" id="ei-file" accept=".json,.csv" style="margin-bottom:10px;font-size:12px">
      <div style="font-size:11px;color:var(--text3);margin-bottom:6px">${esc(t('exportImport.exampleJson'))}</div>
      <pre style="font-size:11px;background:var(--bg3);border-radius:var(--radius);padding:8px;overflow-x:auto;max-height:120px;margin:0 0 8px">${esc(exampleJson)}</pre>
      <div style="font-size:11px;color:var(--text3);margin-bottom:6px">${esc(t('exportImport.exampleCsv'))}</div>
      <pre style="font-size:11px;background:var(--bg3);border-radius:var(--radius);padding:8px;overflow-x:auto;max-height:80px;margin:0">${esc(exampleCsv)}</pre>
    </div>
    ${overageBlock}`;

  const title = importOnly
    ? t('exportImport.modalImport', { label: entityLabel })
    : t('exportImport.modalExportImport', { label: entityLabel });
  openModal(
    title,
    body,
    async () => {
      const fileEl = document.getElementById('ei-file') as HTMLInputElement | null;
      if (!fileEl?.files?.length) {
        closeModal();
        return;
      }
      const fd = new FormData();
      const firstFile = fileEl.files[0];
      if (!firstFile) {
        closeModal();
        return;
      }
      fd.append('file', firstFile);
      // Users : transmet le choix « accepter d'augmenter le plafond » au backend.
      let path = importPath;
      if (entityKey === 'users') {
        const ov = document.getElementById('ei-allow-overage') as HTMLInputElement | null;
        path = `${importPath}?allow_overage=${ov?.checked ? 'true' : 'false'}`;
      }
      try {
        const data = (await api.upload(path, fd)) as Record<string, unknown>;
        closeModal();
        // Message explicite si l'import a été arrêté au plafond (users).
        if (entityKey === 'users' && typeof data['message'] === 'string') {
          toast(data['message'] as string, 'error');
          if (router && typeof router.dispatch === 'function') router.dispatch();
          return;
        }
        const msg = Object.entries(data)
          .map(([k, v]) => `${k}: ${String(v)}`)
          .join(', ');
        toast(t('exportImport.toastImport', { msg }));
        // Recharge la page courante pour voir les nouvelles entrees apparaitre.
        // Avant : import OK mais la liste affichee n'etait pas rafraichie,
        // l'user ne voyait rien et croyait que rien ne s'etait passe (avril
        // 2026). On re-dispatch la route en reinjectant le hash courant.
        if (router && typeof router.dispatch === 'function') {
          router.dispatch();
        }
      } catch (err: unknown) {
        const m = err instanceof Error ? err.message : t('common.error');
        toast(m, 'error');
      }
    },
    t('exportImport.btnImport'),
  );
}

const EXPORT_EXAMPLES: Record<string, ExampleSet> = {
  stages: {
    json: JSON.stringify(
      [
        { name: 'Cadrage', position: 0 },
        { name: 'Build', position: 1 },
      ],
      null,
      2,
    ),
    csv: 'name,position\nCadrage,0\nBuild,1',
  },
  'action-statuses': {
    json: JSON.stringify(
      [
        {
          name: 'En cours',
          position: 0,
          color: '#1a1a1a',
          bold: false,
          italic: false,
          strikethrough: false,
          is_final: false,
        },
        {
          name: 'Terminé',
          position: 1,
          color: '#16a34a',
          bold: false,
          italic: false,
          strikethrough: true,
          is_final: true,
        },
      ],
      null,
      2,
    ),
    csv: 'name,position,color,bold,italic,strikethrough,is_final\nEn cours,0,#1a1a1a,false,false,false,false\nTerminé,1,#16a34a,false,false,true,true',
  },
  'generic-roles': {
    json: JSON.stringify(
      [
        { code: 'CDP', name: 'Chef de projet', is_team: false },
        { code: 'EQUIP', name: 'Équipe projet', is_team: true },
      ],
      null,
      2,
    ),
    csv: 'code,name,is_team\nCDP,Chef de projet,false\nEQUIP,Équipe projet,true',
  },
  users: {
    json: JSON.stringify(
      [
        {
          trigram: 'MAR',
          name: 'Martin Sophie',
          email: 's.martin@ex.com',
          role: 'member',
          is_team: false,
          is_external: false,
        },
      ],
      null,
      2,
    ),
    csv: 'trigram,name,email,role,is_team,is_external\nMAR,Martin Sophie,s.martin@ex.com,member,false,false',
  },
  'risk-lists': {
    json: JSON.stringify(
      [
        {
          name: 'Risques SI',
          description: '',
          risks: [
            {
              stages: ['Build'],
              category: 'Technique',
              visibility: 'publique',
              cause: 'Dette technique',
              consequence: 'Instabilité',
            },
          ],
        },
      ],
      null,
      2,
    ),
    csv: 'list_name,list_description,stages,category,visibility,cause,consequence\nRisques SI,,["Build"],Technique,publique,Dette technique,Instabilité',
  },
  checklists: {
    json: JSON.stringify(
      [
        {
          name: 'Onboarding',
          actions: [
            {
              label: 'Recueillir besoins',
              order: 0,
              accountable_role: 'CDP',
              responsible_roles: ['TECH'],
              consulted_roles: [],
              informed_roles: [],
            },
          ],
        },
      ],
      null,
      2,
    ),
    csv: 'template_name,label,order,accountable_role,responsible_roles,consulted_roles,informed_roles\nOnboarding,Recueillir besoins,0,CDP,["TECH"],[],[]',
  },
};

// ── ESM globals shim (lot B.8 + S4.1) ──
window.showExportRevisionModal = showExportRevisionModal;
window.showExportImportModal = showExportImportModal;
window.EXPORT_EXAMPLES = EXPORT_EXAMPLES;

export {};
